import { useState, type ChangeEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DOCUMENT_KINDS,
  documentKindLabel,
  documentsAreLocked,
  isTerminal,
  MAX_UPLOAD_BYTES,
  UPLOAD_ACCEPT,
  type DealStatus,
  type DealType,
} from '@accel/shared';
import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { Card, ErrorNote, formatDate } from './ui';

export interface DealDocument {
  id: string;
  kind: string;
  filename: string;
  sizeBytes: number;
  createdAt: string;
}

/**
 * Paperwork on a deal, after origination.
 *
 * Until now a document could only be attached at creation time or through the
 * API, which is not how the work actually arrives: a GRN turns up two days
 * after the invoice, and an operator chasing a referred deal needs to add the
 * contract that answers the underwriter's question.
 *
 * What may be attached, and until when, is decided by the server. This
 * component reads the same rules from `@accel/shared` so it never offers a
 * button the API would refuse — an offer that is refused reads as a broken
 * upload rather than a rule the user has run into.
 */
export function DealDocuments({
  dealId,
  dealType,
  status,
  documents,
}: {
  dealId: string;
  dealType: DealType;
  status: DealStatus;
  documents: DealDocument[];
}) {
  const qc = useQueryClient();
  const { can } = useAuth();

  const [file, setFile] = useState<File | null>(null);
  // The paperwork most likely to be missing, given what is being financed.
  const [kind, setKind] = useState<string>(dealType === 'ORDER' ? 'PURCHASE_ORDER' : 'INVOICE');
  const [error, setError] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);
  // Bumped after a successful upload to remount the file input. Clearing it
  // any other way leaves the browser showing the name of a file that has
  // already been sent, which reads as though nothing happened.
  const [inputGeneration, setInputGeneration] = useState(0);

  const closed = isTerminal(status);
  const locked = documentsAreLocked(status);
  const mayAttach = can('deal:create') && !closed;
  const mayRemove = can('deal:create') && !locked;

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose a file to attach');
      const form = new FormData();
      form.append('file', file);
      form.append('kind', kind);
      return api.upload<{ id: string; duplicateOf?: string }>(
        `/deals/${dealId}/documents`,
        form,
      );
    },
    onSuccess: (result) => {
      // The server returns the original row when the same bytes are attached
      // twice. Saying so is more use than a success message for a file that
      // was already there.
      setNote(
        result.duplicateOf
          ? `${file?.name ?? 'That file'} is already attached to this deal`
          : `${file?.name ?? 'Document'} attached`,
      );
      setFile(null);
      setError(null);
      setInputGeneration((n) => n + 1);
      void qc.invalidateQueries({ queryKey: ['deal', dealId] });
      void qc.invalidateQueries({ queryKey: ['deals'] });
      setTimeout(() => setNote(null), 3200);
    },
    onError: (err) => {
      setNote(null);
      setError(err);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/documents/${id}`),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['deal', dealId] });
    },
    onError: (err) => setError(err),
  });

  const fetchDocument = useMutation({
    mutationFn: async (doc: DealDocument) => {
      const { blob, filename } = await api.download(`/documents/${doc.id}/download`);
      // The endpoint serves `attachment` with `nosniff` and needs a bearer
      // token, so the bytes arrive here rather than through a plain link.
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename ?? doc.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    },
    onError: (err) => setError(err),
  });

  function choose(event: ChangeEvent<HTMLInputElement>) {
    const chosen = event.target.files?.[0] ?? null;
    setNote(null);
    setError(null);

    // Checked here as well as on the server: a 20 MB upload that was always
    // going to be refused is a slow way to be told.
    if (chosen && chosen.size > MAX_UPLOAD_BYTES) {
      setFile(null);
      setError(
        new Error(
          `${chosen.name} is ${(chosen.size / 1024 / 1024).toFixed(1)} MB; the limit is ${
            MAX_UPLOAD_BYTES / 1024 / 1024
          } MB`,
        ),
      );
      return;
    }
    setFile(chosen);
  }

  return (
    <>
      <div className="section-title">Documents</div>
      <Card>
        <ErrorNote error={error} />
        {note ? (
          <div
            className="notice"
            style={{
              background: 'var(--blue-soft)',
              color: 'var(--blue)',
              padding: '10px 12px',
              borderRadius: 'var(--r-sm)',
              fontSize: 13,
              marginBottom: 10,
            }}
          >
            {note}
          </div>
        ) : null}

        {documents.length === 0 ? (
          <div className="cell-t2" style={{ paddingBottom: mayAttach ? 12 : 0 }}>
            Nothing attached yet. An invoice with no paperwork behind it is a claim, not a
            receivable.
          </div>
        ) : (
          documents.map((d) => (
            <div key={d.id} className="timeline-item">
              <span style={{ flex: 1 }}>
                <span className="cell-t1">{d.filename}</span>
                <span className="cell-t2" style={{ display: 'block' }}>
                  {documentKindLabel(d.kind)} · {(d.sizeBytes / 1024).toFixed(0)} KB ·{' '}
                  {formatDate(d.createdAt)}
                </span>
              </span>
              <span style={{ display: 'flex', gap: 6 }}>
                <button
                  className="btn btn-ghost"
                  disabled={fetchDocument.isPending}
                  onClick={() => fetchDocument.mutate(d)}
                >
                  Download
                </button>
                {mayRemove ? (
                  <button
                    className="btn btn-ghost"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm(`Remove ${d.filename} from this deal?`)) {
                        remove.mutate(d.id);
                      }
                    }}
                  >
                    Remove
                  </button>
                ) : null}
              </span>
            </div>
          ))
        )}

        {mayAttach ? (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
              marginTop: documents.length > 0 ? 14 : 0,
            }}
          >
            <input
              type="file"
              accept={UPLOAD_ACCEPT}
              aria-label="Attach a document"
              onChange={choose}
              key={inputGeneration}
            />
            <select
              className="select"
              aria-label="Document type"
              value={kind}
              onChange={(e) => setKind(e.target.value)}
            >
              {DOCUMENT_KINDS.map((k) => (
                <option key={k} value={k}>
                  {documentKindLabel(k)}
                </option>
              ))}
            </select>
            <button
              className="btn btn-primary"
              disabled={!file || upload.isPending}
              onClick={() => upload.mutate()}
            >
              {upload.isPending ? 'Attaching…' : 'Attach'}
            </button>
          </div>
        ) : (
          <div className="cell-t2" style={{ marginTop: documents.length > 0 ? 14 : 0 }}>
            {closed
              ? 'Its paperwork is fixed as the record of what was relied on.'
              : can('deal:create')
                ? null
                : 'Your role can read these documents but not change them.'}
          </div>
        )}

        {mayAttach && locked ? (
          <div className="cell-t2" style={{ marginTop: 8 }}>
            This deal has been funded — documents can still be added, but nothing already relied
            on can be removed.
          </div>
        ) : null}
      </Card>
    </>
  );
}
